(() => {
  class ProductBundleComponent {
    constructor(bundleContainer) {
      this.bundleContainer = bundleContainer;
      this.identifier = this.bundleContainer.dataset.bundleIdentifier || '';
      this.connectifCard = this.bundleContainer.querySelector('.connectif-recommendation-card');
      this.addToCartButton = this.bundleContainer.querySelector('.product-bundle-add-to-cart');

      if (!this.bundleContainer || !this.addToCartButton) return;

      this.originalButtonText = this.addToCartButton.textContent;
      this.isProcessing = false;
      this.init();
    }

    init() {
      this.setupConnectifListeners();
      this.setupAddToCartHandler();
      if (!this.tryLoadConnectifData()) {
        this.waitForData();
      }
    }

    waitForData() {
      if (this.retryTimer) return;
      let attempts = 0;
      const maxAttempts = 30;

      this.retryTimer = setInterval(() => {
        attempts += 1;
        if (this.tryLoadConnectifData()) {
          clearInterval(this.retryTimer);
          this.retryTimer = null;
        } else if (attempts >= maxAttempts) {
          clearInterval(this.retryTimer);
          this.retryTimer = null;
        }
      }, 500);
    }

    setupConnectifListeners() {
      if (this.bundleContainer.dataset.bundleConnectifBound === 'true') return;

      document.addEventListener('connectif.ready', () => {
        if (!this.tryLoadConnectifData()) this.waitForData();
      });

      document.addEventListener('connectif.managed.shared_data_received', (event) => {
        if (event && event.detail && event.detail.id === this.identifier) {
          this.processConnectifData();
        }
      });

      this.bundleContainer.dataset.bundleConnectifBound = 'true';
    }

    tryLoadConnectifData() {
      if (window.connectif && window.connectif.managed) {
        return this.processConnectifData();
      }
      return false;
    }

    processConnectifData() {
      if (!window.connectif || !window.connectif.managed || !this.identifier) {
        return false;
      }

      const sharedData = window.connectif.managed.getSharedDataById(this.identifier);
      if (!sharedData || !sharedData.products || sharedData.products.length === 0) {
        return false;
      }

      this.populateRecommendationCard(sharedData.products[0]);
      this.showBundle();
      return true;
    }

    populateRecommendationCard(product) {
      if (!this.connectifCard || !product) return;

      const productHandle = this.extractProductHandle(product.productDetailUrl);
      this.updateCardContent(product, productHandle);
      this.updateCardPricing(product);
      this.showRecommendationCard();
    }

    extractProductHandle(url) {
      if (!url || url.indexOf('/products/') === -1) return '';
      return url.split('/products/')[1];
    }

    updateCardContent(product, productHandle) {
      const image = this.connectifCard.querySelector('.connectif-product-image');
      const name = this.connectifCard.querySelector('.connectif-product-name');
      const link = this.connectifCard.querySelector('.connectif-product-link');

      if (image) {
        image.src = product.imageUrl || '';
        image.alt = product.name || '';
      }

      if (name) {
        name.textContent = product.name || '';
      }

      if (link) {
        link.href = product.productDetailUrl || '#';
        link.setAttribute('data-product-handle', productHandle);
      }

      this.connectifCard.setAttribute('data-product-handle', productHandle);
    }

    updateCardPricing(product) {
      const priceContainer = this.connectifCard.querySelector('.connectif-product-price');
      if (!priceContainer) return;

      const hasDiscount = Number(product.unitPrice) < Number(product.unitPriceOriginal);
      priceContainer.innerHTML = hasDiscount
        ? this.createDiscountPriceHTML(product)
        : this.createRegularPriceHTML(product);
    }

    formatPrice(price) {
      const formatted = new Intl.NumberFormat('es-CO', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        useGrouping: true,
      }).format(price);

      return `$${formatted} COP`;
    }

    createDiscountPriceHTML(product) {
      const currentPrice = this.formatPrice(product.unitPrice);
      const originalPrice = this.formatPrice(product.unitPriceOriginal);
      const discountPercentage = (((product.unitPriceOriginal - product.unitPrice) / product.unitPriceOriginal) * 100).toFixed(2);

      return `
        <span>
          <span class="visually-hidden">Precio regular</span>
          <s>${originalPrice}</s>
        </span>
        <span>-${discountPercentage}%</span>
        <span class="price-item-discount actual_price">
          <span class="visually-hidden">Precio de oferta</span>
          ${currentPrice}
        </span>
      `;
    }

    createRegularPriceHTML(product) {
      return `<span class="actual_price">${this.formatPrice(product.unitPrice)}</span>`;
    }

    showRecommendationCard() {
      if (this.connectifCard) {
        this.connectifCard.style.display = 'flex';
      }
    }

    showBundle() {
      this.bundleContainer.style.display = 'block';
      this.addToCartButton.disabled = false;
    }

    setupAddToCartHandler() {
      if (this.bundleContainer.dataset.bundleAddToCartBound === 'true') return;

      this.addToCartButton.addEventListener('click', async (e) => {
        e.preventDefault();
        if (this.addToCartButton.disabled || this.isProcessing) return;

        if (typeof gtag !== 'undefined') {
          const heading = this.bundleContainer.querySelector('.product-bundle-heading');
          gtag('event', 'use_bundle', {
            event_category: 'Engagement',
            event_label: heading ? heading.textContent.trim() : 'Bundle Product Click',
            bundle_identifier: this.identifier || undefined,
          });
        }

        this.isProcessing = true;
        this.setLoadingState(true);

        try {
          const variantIds = await this.getVariantIds();
          if (variantIds.length === 0) throw new Error('No variants found');

          await this.addItemsToCart(variantIds);
          this.updateMiniCart();
        } catch (error) {
          console.error('Bundle add to cart error:', error);
          alert('Error adding products to cart. Please try again.');
        } finally {
          this.setLoadingState(false);
          this.isProcessing = false;
        }
      });

      this.bundleContainer.dataset.bundleAddToCartBound = 'true';
    }

    async getVariantIds() {
      const visibleCards = Array.from(this.bundleContainer.querySelectorAll('.product-bundle-card')).filter((card) => {
        return getComputedStyle(card).display !== 'none';
      });

      const variantIds = [];
      for (const card of visibleCards) {
        const productHandle = card.dataset.productHandle;
        if (productHandle) {
          const variantId = await this.fetchFirstAvailableVariant(productHandle);
          if (variantId) variantIds.push(variantId);
        }
      }

      return variantIds;
    }

    async fetchFirstAvailableVariant(productHandle) {
      try {
        const response = await fetch(`${window.Shopify.routes.root}products/${productHandle}.js`);
        if (!response.ok) return null;

        const productData = await response.json();
        const availableVariant = productData.variants.find((variant) => variant.available);

        if (availableVariant) return availableVariant.id.toString();
        return productData.variants.length > 0 ? productData.variants[0].id.toString() : null;
      } catch (error) {
        console.error(`Error fetching variant for ${productHandle}:`, error);
        return null;
      }
    }

    async addItemsToCart(variantIds) {
      const items = variantIds.map((variantId) => ({ id: parseInt(variantId, 10), quantity: 1 }));

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add items to cart');
      }

      return response.json();
    }

    updateMiniCart() {
      if (!window.$ || !window.jQuery) return;

      $.get(`${window.Shopify.routes.root}cart?view=minicart`, (itemData) => {
        const itemCount = jQuery(itemData).find('#cart-notification-button .count').text();

        if ($('.cart-count-bubble').length > 0) {
          $('.cart-count-bubble span').text(itemCount);
        } else {
          $('#cart-icon-bubble').append('<div class="cart-count-bubble"><span></span></div>');
          $('.cart-count-bubble span').text(itemCount);
        }

        $('#cart-notification').text('').append("<div id='min-cart-replace'></div>");
        $('#cart-notification').replaceWith(itemData);
        $('#cart-notification').addClass('minicart-active');
      });
    }

    setLoadingState(isLoading) {
      this.addToCartButton.disabled = isLoading;
      this.addToCartButton.textContent = isLoading ? 'Agregando...' : this.originalButtonText;
    }
  }

  function initProductBundleComponents(scope) {
    const root = scope instanceof Element || scope instanceof Document ? scope : document;
    const containers = root.querySelectorAll('[data-bundle-component]');

    containers.forEach((container) => {
      if (container.dataset.bundleInitialized === 'true') return;
      // Keep hidden until Connectif data is ready.
      container.dataset.bundleInitialized = 'true';
      new ProductBundleComponent(container);
    });
  }

  function scheduleInit(scope) {
    initProductBundleComponents(scope || document);
    setTimeout(() => initProductBundleComponents(scope || document), 150);
    setTimeout(() => initProductBundleComponents(scope || document), 600);
  }

  document.addEventListener('DOMContentLoaded', () => {
    scheduleInit(document);
  });

  document.addEventListener('shopify:section:load', (event) => {
    scheduleInit(event.target);
  });

  document.addEventListener('shopify:section:select', (event) => {
    scheduleInit(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    scheduleInit(event.target);
    const bundle = event.target.querySelector('[data-bundle-component]');
    if (!bundle) return;
    bundle.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const bundle = event.target.querySelector('[data-bundle-component]');
    if (!bundle) return;
  });

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        if (node.matches('[data-bundle-component]')) {
          scheduleInit(node.parentElement || node);
          return;
        }

        if (node.querySelector && node.querySelector('[data-bundle-component]')) {
          scheduleInit(node);
        }
      });
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  window.initProductBundleComponents = initProductBundleComponents;
})();
